import { Injectable } from "@angular/core";
import { BehaviorSubject, map } from "rxjs";
import { ExpressionGroups, VariableDetail } from "#models/variables/variableDetail.model";
import { Observable, firstValueFrom } from "rxjs";
import { InputTypes } from "helpers/enums/inputTypes.enum";
import { VariablesService } from "./variables.service";
import { Question } from "#models/job-templates/section";
import { LogicQuestionService } from "./logic-question.service";
import { CalculationVariable } from "#models/variables/calculation-variables.model";
import { QuestionService } from "./question.service";

@Injectable({
    providedIn: "root",
  })

  export class VariableDetailService {

    private basicLookupSubject = new BehaviorSubject<VariableDetail>(null);
    basicLookupData = this.basicLookupSubject.asObservable();

    private primaryInputSubject = new BehaviorSubject<VariableDetail>(null);
    primaryInputData = this.primaryInputSubject.asObservable();

    private secondaryInputSubject = new BehaviorSubject<VariableDetail>(null);
    secondaryInputData = this.secondaryInputSubject.asObservable();

    private multiplyLookupSubject = new BehaviorSubject<ExpressionGroups[]>(null);
    multiplyLookupData = this.multiplyLookupSubject.asObservable();

    private showWindowSubject = new BehaviorSubject<boolean>(null);
    showWindowStatus = this.showWindowSubject.asObservable();

    constructor(
      private readonly variablesSvc: VariablesService,
      private readonly questionSvc: QuestionService,
      private readonly logicQuestionSvc: LogicQuestionService
    ) { }

    setValuesforBasicLookup(data: VariableDetail){
      this.basicLookupSubject.next(data);
    }

    setValuesforPrimaryInput(data: VariableDetail){
      this.primaryInputSubject.next(data);
    }

    setValuesforSecondaryInput(data: VariableDetail){
      this.secondaryInputSubject.next(data);
    }

    setValuesForMultiplyLookup(data: ExpressionGroups[]) {
      this.multiplyLookupSubject.next(data);
    }

    setValueForWindowStatus(data: boolean) {
      this.showWindowSubject.next(data);
    }

    removeValuesForAllSubjects(): void {
      this.primaryInputSubject.next(null);
      this.secondaryInputSubject.next(null);
      this.multiplyLookupSubject.next(null);
    }

    getInputData(inputType: number, inputID: string, jobTemplateId: string): Observable<any>  {
    
        switch (inputType) {
          case InputTypes.StaticLookupVariable:
            return this.getInitialLookupData(jobTemplateId, 
              inputID);
          break;
          case InputTypes.SingleLookupVariable:
            return this.getInitialLookupData(jobTemplateId, 
              inputID);
          break;
          case InputTypes.DoubleLookupVariable:
            return this.getInitialLookupData(jobTemplateId, 
              inputID);
          break;
          case InputTypes.RangeVariable:
            return this.getInitialRangeData(jobTemplateId, 
              inputID);
            break;
          case InputTypes.Question:
            return this.getInitialQuestionData(jobTemplateId, 
              inputID);
          break;
          case InputTypes.MultiplyVariable:
            return this.getInitialMultiplyData(jobTemplateId, 
              inputID);
          break;
          case InputTypes.CalculationVariable:
            return this.getInitialCalculationData(inputID);
          break;
        }
      }

      getInitialLookupData(jobTemplateID: string, itemID: string): Observable<VariableDetail> {
      
         return this.variablesSvc
            .getLookups({
            jobTemplateId: jobTemplateID,
            expand: true
          }).pipe(
            map((lookupData) => {
                  
              let lookupDataFiltered: VariableDetail;
              const filteredLookupData = lookupData.filter(item => item.ID === itemID);
              lookupDataFiltered = filteredLookupData[0];
      
              return lookupDataFiltered as VariableDetail;
        }))
      }
      
      getInitialRangeData(jobTemplateID: string, itemID: string): Observable<VariableDetail> {
      
        return this.variablesSvc.getRange({
          jobTemplateId: jobTemplateID,
          expand: true
        }).pipe(
          map((rangeData) => {
      
            let rangeDataFiltered: VariableDetail;
            const filteredRangeData = rangeData.filter(item => item.ID === itemID);
            rangeDataFiltered = filteredRangeData[0];
      
            return rangeDataFiltered;
          }));
      }
      
      getInitialMultiplyData(jobTemplateID: string, itemID: string): Observable<VariableDetail> {
      
        return this.variablesSvc
           .getMultiplier({
           jobTemplateId: jobTemplateID,
           expand: true
         }).pipe(
          map((multiplyData) => {

           let multiplyDataFiltered: VariableDetail;

           if (Array.isArray(multiplyData)){            
            const filteredMultiplyData = multiplyData.filter(item => item.ID === itemID);
            multiplyDataFiltered = filteredMultiplyData[0];
           }
           else {
            multiplyDataFiltered = multiplyData
           }

           return multiplyDataFiltered;
       }))
      }
      
      getInitialQuestionData(jobTemplateID: string, itemID: string): Observable<Question> {  
      
        return this.questionSvc
           .get(jobTemplateID, null, true)
            .pipe(
              map((questionData) => {
          
           let questionDataFiltered: Question;
           const filteredLookupData = questionData.filter(item => item.ID === itemID);
           questionDataFiltered = filteredLookupData[0];
      
           return questionDataFiltered;
       }))
      }
      
      getInitialCalculationData(itemID: string): Observable<CalculationVariable> {
      
        return this.variablesSvc
          .getSingleCalculation(itemID)
          .pipe(
            map(
              (calculationVariableData) => {
      
           return calculationVariableData;
       }))
      }

      getLogicVariableDetails(jobTemplateID: string, itemID: string): Observable<string> {

        return this.logicQuestionSvc
        .get({
          jobTemplateId: jobTemplateID,
          expand: false
        })
        .pipe(
          map(
            (logicData) => {

            let logicDependencyName: string = null;

            if (Array.isArray(logicData)){
              const filteredLookupData = logicData.filter(item => item.ID === itemID);
              logicDependencyName = filteredLookupData[0].Name;
            }
            else {
              logicDependencyName = logicData.Name
            }
  
            return logicDependencyName;
          })
        )
      }
}